import { useEffect, useState, type FormEvent } from 'react';
import { isApiError } from '../../lib/api';
import {
  createModelPrice,
  deleteModelPrice,
  listModelPrices,
  updateModelPrice,
  type ModelPrice,
} from '../../lib/ai-quality-api';
import { getSystemConfig, putSystemConfig } from '../../lib/webhooks-api';
import styles from './admin.module.css';
import { confirmDialog } from '../../stores/confirm';

const usd = (n: number) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 6 })}`;

/** Empty → null; anything else must be a non-negative number. */
function parseAmount(raw: string): number | null | 'invalid' {
  const t = raw.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : 'invalid';
}

// ─── AI costs (super-admin) ────────────────────────────────────────────────
// The two inputs of the cost-per-resolution report: what each model costs per
// million tokens, and what an hour of agent time costs. Usage is priced when
// the report is read, so a corrected price applies to past usage too; a price
// that changes from some date on is a new row with that date.
export function AiCostsSection() {
  const [prices, setPrices] = useState<ModelPrice[] | null>(null);
  const [hourly, setHourly] = useState('');
  const [savedHourly, setSavedHourly] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);

  async function load() {
    try {
      const [p, cfg] = await Promise.all([listModelPrices(), getSystemConfig()]);
      setPrices(p);
      setSavedHourly(cfg.agentHourlyCostUsd);
      setHourly(cfg.agentHourlyCostUsd == null ? '' : String(cfg.agentHourlyCostUsd));
      setError(null);
    } catch {
      setError('Could not load AI costs.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function saveHourly(e: FormEvent) {
    e.preventDefault();
    const v = parseAmount(hourly);
    if (v === 'invalid') {
      setError('Enter the hourly cost as a number, or leave it empty.');
      return;
    }
    setError(null);
    try {
      const cfg = await putSystemConfig({ agentHourlyCostUsd: v });
      setSavedHourly(cfg.agentHourlyCostUsd);
      setNotice('Agent hourly cost saved.');
    } catch {
      setError('Could not save the hourly cost.');
    }
  }

  async function remove(p: ModelPrice) {
    if (!(await confirmDialog({ title: `Delete the price for ${p.provider} / ${p.model}?`, message: 'Its usage will show as unpriced.' }))) return;
    try {
      await deleteModelPrice(p.id);
      await load();
    } catch {
      setError('Could not delete the price.');
    }
  }

  return (
    <>
      <section className={styles.section}>
        <p className={styles.kicker}>Agent time</p>
        <p style={{ color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-xs)', marginTop: 0 }}>
          Blends agent handling time into the cost per resolution on Analytics. Leave empty to report AI cost only.
        </p>
        <form className={styles.form} onSubmit={saveHourly}>
          <label className={styles.field}>
            <span className={styles.label}>Agent cost per hour (USD)</span>
            <input
              className={styles.input}
              inputMode="decimal"
              value={hourly}
              onChange={(e) => setHourly(e.target.value)}
              placeholder="Not set"
            />
            <span className={styles.hint}>
              {savedHourly == null ? 'Not set — AI cost only.' : `Saved: ${usd(savedHourly)} per hour.`}
            </span>
          </label>
          <div className={styles.formActions}>
            <button type="submit" className={styles.button}>
              Save hourly cost
            </button>
          </div>
        </form>
      </section>

      <section className={styles.section}>
        <p className={styles.kicker}>Model prices</p>
        <p style={{ color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-xs)', marginTop: 0 }}>
          USD per million tokens. A model name also prices its dated variants (claude-sonnet-4-5 covers
          claude-sonnet-4-5-20250929). Models without a price — self-hosted ones, usually — are left out of the totals
          and counted as unpriced.
        </p>
        {error && <p className={styles.error} role="alert">{error}</p>}
        {notice && <p className={styles.notice}>{notice}</p>}
        <div className={styles.tableWrap}>
          <table className={styles.table} aria-label="Model prices">
            <thead>
              <tr>
                <th>Provider</th>
                <th>Model</th>
                <th>Input / Mtok</th>
                <th>Output / Mtok</th>
                <th>From</th>
                <th>Source</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(prices ?? []).map((p) =>
                editing === p.id ? (
                  <PriceEditRow
                    key={p.id}
                    price={p}
                    onCancel={() => setEditing(null)}
                    onSaved={() => {
                      setEditing(null);
                      void load();
                    }}
                    onError={setError}
                  />
                ) : (
                  <tr key={p.id}>
                    <td>{p.provider}</td>
                    <td className={styles.mono}>{p.model}</td>
                    <td>{usd(p.inputPerMtok)}</td>
                    <td>{usd(p.outputPerMtok)}</td>
                    <td>{new Date(p.effectiveFrom).getUTCFullYear() <= 2000 ? 'always' : new Date(p.effectiveFrom).toLocaleDateString()}</td>
                    <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.source ?? ''}>
                      {p.source ?? '—'}
                    </td>
                    <td>
                      <div className={styles.actions}>
                        <button type="button" className={styles.buttonGhost} onClick={() => setEditing(p.id)}>
                          Edit
                        </button>
                        <button type="button" className={styles.buttonDanger} onClick={() => void remove(p)}>
                          delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ),
              )}
              {prices?.length === 0 && (
                <tr>
                  <td colSpan={7}>No prices yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <NewPriceForm onAdded={() => void load()} />
      </section>
    </>
  );
}

function PriceEditRow({
  price,
  onCancel,
  onSaved,
  onError,
}: {
  price: ModelPrice;
  onCancel: () => void;
  onSaved: () => void;
  onError: (msg: string) => void;
}) {
  const [input, setInput] = useState(String(price.inputPerMtok));
  const [output, setOutput] = useState(String(price.outputPerMtok));
  const [source, setSource] = useState(price.source ?? '');

  async function save() {
    const i = parseAmount(input);
    const o = parseAmount(output);
    if (typeof i !== 'number' || typeof o !== 'number') {
      onError('Prices must be numbers of zero or more.');
      return;
    }
    try {
      await updateModelPrice(price.id, { inputPerMtok: i, outputPerMtok: o, source: source.trim() || null });
      onSaved();
    } catch {
      onError('Could not save the price.');
    }
  }

  return (
    <tr>
      <td>{price.provider}</td>
      <td className={styles.mono}>{price.model}</td>
      <td>
        <input className={styles.input} aria-label="Input price" inputMode="decimal" value={input} onChange={(e) => setInput(e.target.value)} />
      </td>
      <td>
        <input className={styles.input} aria-label="Output price" inputMode="decimal" value={output} onChange={(e) => setOutput(e.target.value)} />
      </td>
      <td />
      <td>
        <input className={styles.input} aria-label="Source" value={source} onChange={(e) => setSource(e.target.value)} />
      </td>
      <td>
        <div className={styles.actions}>
          <button type="button" className={styles.button} onClick={() => void save()}>
            Save
          </button>
          <button type="button" className={styles.buttonGhost} onClick={onCancel}>
            Cancel
          </button>
        </div>
      </td>
    </tr>
  );
}

function NewPriceForm({ onAdded }: { onAdded: () => void }) {
  const [provider, setProvider] = useState('');
  const [model, setModel] = useState('');
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [from, setFrom] = useState('');
  const [source, setSource] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const i = parseAmount(input);
    const o = parseAmount(output);
    if (!provider.trim() || !model.trim() || typeof i !== 'number' || typeof o !== 'number') {
      setError('Provider, model and both prices are required; prices are numbers of zero or more.');
      return;
    }
    setError(null);
    try {
      await createModelPrice({
        provider: provider.trim(),
        model: model.trim(),
        inputPerMtok: i,
        outputPerMtok: o,
        ...(from ? { effectiveFrom: new Date(`${from}T00:00:00Z`).toISOString() } : {}),
        source: source.trim() || null,
      });
      setProvider('');
      setModel('');
      setInput('');
      setOutput('');
      setFrom('');
      setSource('');
      onAdded();
    } catch (err) {
      setError(
        isApiError(err) && err.status === 409
          ? 'That model already has a price from that date — edit it instead.'
          : 'Could not add the price.',
      );
    }
  }

  return (
    <form className={styles.form} onSubmit={submit} aria-label="Add model price" style={{ marginTop: 'var(--inic-space-4)' }}>
      <label className={styles.field}>
        <span className={styles.label}>Provider</span>
        <input className={styles.input} value={provider} onChange={(e) => setProvider(e.target.value)} placeholder="openline" />
      </label>
      <label className={styles.field}>
        <span className={styles.label}>Model</span>
        <input className={styles.input} value={model} onChange={(e) => setModel(e.target.value)} placeholder="qwen3:14b" />
      </label>
      <label className={styles.field}>
        <span className={styles.label}>Input USD / Mtok</span>
        <input className={styles.input} inputMode="decimal" value={input} onChange={(e) => setInput(e.target.value)} />
      </label>
      <label className={styles.field}>
        <span className={styles.label}>Output USD / Mtok</span>
        <input className={styles.input} inputMode="decimal" value={output} onChange={(e) => setOutput(e.target.value)} />
      </label>
      <label className={styles.field}>
        <span className={styles.label}>Effective from</span>
        <input className={styles.input} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        <span className={styles.hint}>Empty = applies to all usage.</span>
      </label>
      <label className={styles.field}>
        <span className={styles.label}>Source</span>
        <input className={styles.input} value={source} onChange={(e) => setSource(e.target.value)} placeholder="Where the number came from" />
      </label>
      <div className={styles.formActions}>
        <button type="submit" className={styles.button}>
          Add price
        </button>
      </div>
      {error && <p className={styles.error} style={{ gridColumn: '1 / -1' }}>{error}</p>}
    </form>
  );
}
